import {useEffect, useMemo, useState, useCallback} from 'react'
import {useAuth} from './modules/auth'
import {useConsumerWidget} from './context/ConsumerWidgetContext'

/**
 * script with contact attributes:
  `<script type="text/javascript">
    (function(w, d, x, id){
      s=d.createElement('script');
      s.src='https://adastra-qas.my.connect.aws/connectwidget/static/amazon-connect-chat-interface-client.js';
      s.async=1;
      s.id=id;
      d.getElementsByTagName('head')[0].appendChild(s);
      w[x] =  w[x] || function() { (w[x].ac = w[x].ac || []).push(arguments) };
    })(window, document, 'amazon_connect', '65cecdde-5a9e-405c-b781-8bfbc63431ca');
    amazon_connect('styles', { iconType: 'VOICE', openChat: { color: '#ffffff', backgroundColor: '#123456' }, closeChat: { color: '#ffffff', backgroundColor: '#123456'} });
    amazon_connect('snippetId', 'QVFJREFIaXdlaTllQXR1SnQyK1JZc1Z3dWE3clBZQjQvWm15emlVb25scEltc3BJNmdHUHVVU3ZIUURkVjJGVzhxNTk1RTk1QUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNcXNOWmdwM2RzWGhMT1ZvY0FnRVFnQ3RVWk4xVjFqa2xaTFlRVXNod2JzRGpoYVFodytnVGt1M3FMUS8wSGpjTFhBakJmNWJYTW9sWGFwVXA6OktOTWlxY2EzTWZtZUNFUXhKOUNrMXh0R3R0Y09lUzBldGJqYXJaNmdVKzZ0QXYrYlFOdG5LZGtEc1FlZFJUVExGYkdaY3ZXTmpYdDR3cFl5RlJDRys4K3NzSk5CaklUT2M3dkdoTXJWZXVqdFNoR2trOXZnb1ltZ2xFSXljNjJ4a0M5aXRNaitiT2JrOTRua3JIcy9id2dhMFYyUXdBbz0=');
  amazon_connect('supportedMessagingContentTypes', ['text/plain', 'text/markdown', 'application/vnd.amazonaws.connect.message.interactive', 'application/vnd.amazonaws.connect.message.interactive.response']);
  amazon_connect('contactAttributes', ${attributes});

  </script>`
 * 
 */

const getAttributesAsString = (attributes: Record<string, string>) => {
  console.log('=== getAttributesAsString', attributes)

  return Object.entries(attributes)
    .map(([key, value]) => `${key}: '${value}'`)
    .join(', ')
}

const getConnectInstance = (): 'prod' | 'dev' | 'qas' => {
  const apiUrl = process.env.REACT_APP_API_URL || ''
  const serverEnv = process.env.REACT_APP_SERVER || ''
  const apiUrlLower = apiUrl.toLowerCase()
  const serverEnvLower = serverEnv.toLowerCase()

  if (
    apiUrlLower.includes('prd') ||
    apiUrlLower.includes('prod') ||
    apiUrlLower.includes('production') ||
    serverEnvLower === 'prd' ||
    serverEnvLower === 'prod' ||
    serverEnvLower === 'prod-v2' ||
    serverEnvLower === 'production'
  ) {
    return 'prod'
  }

  if (
    apiUrlLower.includes('dev') ||
    apiUrlLower.includes('localhost') ||
    apiUrlLower.includes('127.0.0.1') ||
    serverEnvLower === 'dev' ||
    serverEnvLower === 'development'
  ) {
    return 'dev'
  }

  return 'qas'
}

// Amazon Connect configuration for different environments
const getConnectConfig = () => {
  const instance = getConnectInstance()
  switch (instance) {
    case 'prod':
      // Production instance configuration
      return {
        scriptUrl: 'https://adastra-prd.my.connect.aws/connectwidget/static/amazon-connect-chat-interface-client.js',
        widgetId: 'ddfdd04c-f28b-4d8a-9767-b38157865b81',
        snippetId: 'QVFJREFIaXdlaTllQXR1SnQyK1JZc1Z3dWE3clBZQjQvWm15emlVb25scEltc3BJNmdIL3p6ZHpENkd4VVQ5b01ZYW92NUl3QUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNbUlEMU94Wk9WWHRWbkZDYkFnRVFnQ3NIT1lka2pFSWlMV2ZTSVYyOEVIcEF6SnkxRnpiV3ZuNmprWjBIOVYwdjNueWFQVDE3T05WNFJvQzk6OnNJY2pEbnNkRkRLZEFRUGR2NDRXUzIxYmpxRGsyQ3NyVFVzS3VaZW8rYmNxbFlxakYxL3FTYUJRR1dwaGd3NVRGOWxnQnJOVUV2T3JwT3ZTbWZwa2xCQ1NYZ2NPVGlVMzYyVmkrT2NrQld0NXBhZDI1T0dOWmZFbGVyRkx6VVZFN1BZeVppenFqbUM3UUYrZlJZcytJOUpYRUlIZ2pMbz0='
      }
    case 'dev':
        return {
          scriptUrl: 'https://adastra-sandbox3.my.connect.aws/connectwidget/static/amazon-connect-chat-interface-client.js',
          widgetId: '2860e39c-75f2-485c-b974-2e06d172f32b',
          snippetId: 'QVFJREFIaWFZYXRVSlpIekdkUUg5YXhZenVQMktKRXNIWTVFQWpBYVErTEdzRnpvZHdGSzNrZThsbXh3UTV0bEdjeGJBb2R0QUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNUkN1ZG5HZGtNdllkUjhzdkFnRVFnQ3QwWDE1anNHZ0RYQUtrSEFFVHExRXk1VG12NzZXZDhlb05ZTEJIWGgzeUUrbUozNFp1c29oYklYb2o6Okwrb3B6VUdUQWhQVUhqc0hlM0RwM1kzNzFsdW5ySDRNMzhBcmpYak50cWNZUXJpZFBLdGlNQzJET3dlYWdNSUxJWCtEZlYvSS9YZlNQNW41cytWNzVDYWxlSmxUUm01Wm9vZnIvMGhvUHVZdFQ5b21XQ3lRcjVFM2lENkRyS2lEQTdjR2tFd2VvVE1wd0hLNzFhVk9NSDR3OVUrVjg5ST0='
        }
      
    case 'qas':
    default:
        return {
          scriptUrl: 'https://adastra-qas.my.connect.aws/connectwidget/static/amazon-connect-chat-interface-client.js',
          widgetId: 'b25bbcdb-bd99-4887-9c2a-71a8f62d4580',
          snippetId: 'QVFJREFIaWFZYXRVSlpIekdkUUg5YXhZenVQMktKRXNIWTVFQWpBYVErTEdzRnpvZHdHRVFjZEJGNGM4Q0szbTVsL1V2dEhqQUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNZUtEbE5udDdoMCtCaHhweEFnRVFnQ3VuVXJFQnpxQlJEbkVrUHhRV2JaVjFzV2lEMGNvTlBTUERNUVkyN1VmbzM1eWdNZHg5dXdiV2Y3S2g6OldsU2RDbUFJV2tvUFBHNWpzN1RYdWRwaEo4dkRsUTJqbm5nOWFRSm5BSTM5QkI0SmZFWDZ4YTB0S2NJMlM0MnQ3cytjRmtEbngrSDQwS2dlWDBZUG1sbU44T014TWV2KzErN1lhVERxY2lRRjVPbndtN1Y4OTdJOFdENmRtdjA2cU5FMVkxOWJqd1N0ZVo5WG9URE1kNXZYZlB2U1JJQT0='
      }
  }
}

const getConnectConfigVideo = () => {
  const instance = getConnectInstance()
  switch (instance) {
    case 'prod':
      // Production instance configuration
      return {
        scriptUrl: 'https://adastra-prd.my.connect.aws/connectwidget/static/amazon-connect-chat-interface-client.js',
        widgetId: 'fa9571f6-5674-434b-8141-9610d4ab705d',
        snippetId: 'QVFJREFIaXdlaTllQXR1SnQyK1JZc1Z3dWE3clBZQjQvWm15emlVb25scEltc3BJNmdGeEtHdlFkbnZ5YWE0ekVEYzRuWVNuQUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNMTFINHFVRDJmU2JEZmR6V0FnRVFnQ3ZrU0Myb0R2Y05DWHF6SGRlOXZLOTdGZ2NjRDhCWXM0eU1ZaXBWTlFRbVNJVGxxZldaNk5Cd1lZZ0g6OmpYWDQzSUczZzl6b2o1dkNmVFRvVHFNcnI0TXdOV2tYSmUxNFZHRVMvRU1zWHVJenIvYkFaQ1c0TmxtdUJUK2xQVnY2Z2ZiOTJPcFVCVjhlcXVQRVBtVS9YdklQZ05LR1hBUGVGaFc0amlBK3R1M2E1b2Y4TzRLYk05KzA3YVhKT2dmY0hlTk9HSTNwM0paNWs0QmxUVmNRUzRSM01ZRT0='
      }
    case 'dev':
        return {
          scriptUrl: 'https://adastra-sandbox3.my.connect.aws/connectwidget/static/amazon-connect-chat-interface-client.js',
          widgetId: '4830be87-12ee-44a4-ab23-703914cba7d0',
          snippetId: 'QVFJREFIaXdlaTllQXR1SnQyK1JZc1Z3dWE3clBZQjQvWm15emlVb25scEltc3BJNmdHcm52OGNYdFMxRGFPYXRaMG1YdFR0QUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNN2N2eFlKTnB6aXcvSi9BbUFnRVFnQ3RwNEdhbDVWTlhzeWIxbzFiVjZGd3Zscjg1QmY5MDhHTDFJRkttdExTU3hoRTRqM3dhSDI5VHo3K086Ono1WG5WUnBCY0FNU09OelBPQWtiaUMzWGcreTBmWFcyK0NKQkJPSURWYXNSbmYyelpORU1rek5KZ1J2VlE4TlRrYmViVzhEZ3R1RW1uWWJCbFlsVml5NHRtazhZM2JXeUlUeVRRMjByUnJXeDE4TEF1QTRTamVhcjBOQnlZWEs4ZHZIYXd1YzZIN0hLM0xwd084N2ZJZ0tKS3l1bXNJVT0='
        }
      // Dev instance configuration
   
    case 'qas':
    default:
      return {
        scriptUrl: 'https://adastra-qas.my.connect.aws/connectwidget/static/amazon-connect-chat-interface-client.js',
        widgetId: '65cecdde-5a9e-405c-b781-8bfbc63431ca',
        snippetId: 'QVFJREFIaXdlaTllQXR1SnQyK1JZc1Z3dWE3clBZQjQvWm15emlVb25scEltc3BJNmdHUHVVU3ZIUURkVjJGVzhxNTk1RTk1QUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNcXNOWmdwM2RzWGhMT1ZvY0FnRVFnQ3RVWk4xVjFqa2xaTFlRVXNod2JzRGpoYVFodytnVGt1M3FMUS8wSGpjTFhBakJmNWJYTW9sWGFwVXA6OktOTWlxY2EzTWZtZUNFUXhKOUNrMXh0R3R0Y09lUzBldGJqYXJaNmdVKzZ0QXYrYlFOdG5LZGtEc1FlZFJUVExGYkdaY3ZXTmpYdDR3cFl5RlJDRys4K3NzSk5CaklUT2M3dkdoTXJWZXVqdFNoR2trOXZnb1ltZ2xFSXljNjJ4a0M5aXRNaitiT2JrOTRua3JIcy9id2dhMFYyUXdBbz0='
      }
  }
}

const getConnectConfigAiInterpretation = () => {
  const instance = getConnectInstance()

  switch (instance) {
    case 'prod':
      return {
        scriptUrl: 'https://adastra-prd.my.connect.aws/connectwidget/static/amazon-connect-chat-interface-client.js',
        widgetId: '77458b06-45c2-4039-8b1f-dfa168059303',
        snippetId: 'QVFJREFIaThPZ042Z2Vwd1EzbWNOOW9zZVBpdlltbFlYREFwbEVldGoySmJEcktXREFHODYyUnh4ZnUwRUhCQUxNUUttL0FxQUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNa3ZjRTJaS2s1Vm56N2tzT0FnRVFnQ3M0OG1DRnBXTnBHcTRXQUJ0Vm1jUTNyQzZEd1dORU80dHF5VnZwVnl4bW5mODdabHBNNnFZMlFEMm06OnhyYjBvdy92TXpLK1I2TU5OZmVaZGlYcm9NdnVrVitjQ3N3RUJkRHhiQVNNUk42d0YvN1lHT2VsZmtEcTcvNEloK1MzbU9RNGlTRGhJWWVyajRDOTR0TUpYdkVaUDRMaCtzc1UyZnE3YXRkQXpFY09pYmRXNktWRk1IdEVqbU9HUnJnMFRvS3RnRWU3MExnU3NxbjRMQVVsVHFhQVJ4UT0=',
        openChatBackgroundColor: '#123456',
      }
    case 'dev':
      return {
        scriptUrl: 'https://adastra-sandbox3.my.connect.aws/connectwidget/static/amazon-connect-chat-interface-client.js',
        widgetId: 'c2273005-2a2a-40d0-9ce3-fe5ca7d8d1f0',
        snippetId: 'QVFJREFIaThPZ042Z2Vwd1EzbWNOOW9zZVBpdlltbFlYREFwbEVldGoySmJEcktXREFFSzRLUW44eWsrNGV3K056ZlhDZTZsQUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNWTF3SEsrY25rNjZ4T2ZmdkFnRVFnQ3ZXUFBKdjZTUURkQmJTUnViVGVNVkZ4NGgwUS9hU1ZaUDR3dW1tbVdEdXVqYWVQNXlLcndYU2V5RG46OlJRK2lObTgrZnlwMzY3TjkvVDU3Uzg1V3BLeGpOZlBKU3dBN3hxWUlEK0gzOURWeFNUeVFlZC8veEhVTUFiU1RPYklWUUkrWFNxQnIrbGFjL203Mno1blV0SDhvRGpMZXRuTGFzU0ZpVVg5NjJmUHYvbGtnNkdnZTFHYnlRTEVsWk0vTE03QklzYzBoWU54NXpvdVdUWnovQVEvTDR1dz0=',
        openChatBackgroundColor: '#123456',
      }
    case 'qas':
    default:
      return {
        scriptUrl: 'https://adastra-qas.my.connect.aws/connectwidget/static/amazon-connect-chat-interface-client.js',
        widgetId: 'b38c26a7-00f8-4180-b71a-77c20780c677',
        snippetId: 'QVFJREFIaThPZ042Z2Vwd1EzbWNOOW9zZVBpdlltbFlYREFwbEVldGoySmJEcktXREFFM0MxT1E2c0RURkJBK0hEYjUvZkE1QUFBQWJqQnNCZ2txaGtpRzl3MEJCd2FnWHpCZEFnRUFNRmdHQ1NxR1NJYjNEUUVIQVRBZUJnbGdoa2dCWlFNRUFTNHdFUVFNTitYU3cxRFBkMUptM1FvdUFnRVFnQ3VKN3RkRmJvQzdoclJYY08xNEMvaGJRNUlwUkpyUUVQU0UxNjVNWWl3ZHgzNXRzcUtQTCtlQ0l5SmQ6Ok9uRVNmYTQ1Wmc5Y1NMZFJnWmFoeGs5Uk1YZVJoaTRzNUhuR2tWeGhDclZ3WG1HWUdhVU5pVWxMOHdqNXZ6c3g2OXhQWVhSTWV1dkVQQXRpQkQ5a2hVbEg4cEo2V0ttSFZMN0lFT0Z3UkZvd3NMcFRoWXk1RjZGMXFsTy9kWXBGenppNWZGZ1Zoc1BYbG8vbHU3M2E5OFRBOTZrK2Zacz0=',
        openChatBackgroundColor: '#535412',
      }
  }
}

// Note: We no longer manually initialize after load; we pre-queue config to the stub

const RequesterWidget = () => {
  const {currentUser} = useAuth()
  const {getPreCallConfig, placeCall, setPlaceCall, callMode} = useConsumerWidget()
  const connectConfig = useMemo(() => getConnectConfig(), [])
  const connectConfigVideo = useMemo(() => getConnectConfigVideo(), [])
  const connectConfigAi = useMemo(() => getConnectConfigAiInterpretation(), [])
  const {type, gender, language, transcribe, record, meetingPin} = getPreCallConfig
  const userId = currentUser?.result?.awsUserId
  const isAiInterpretationCall = callMode === 'ai-interpretation'

  const activeConnectConfig = isAiInterpretationCall
    ? connectConfigAi
    : type === 'audio'
      ? connectConfig
      : connectConfigVideo
  const openChatBackgroundColor = isAiInterpretationCall
    ? connectConfigAi.openChatBackgroundColor
    : '#123456'

  const attributes = useMemo(() => {
    return {
      AWSUserId: userId ?? '',
      serviceType: currentUser?.result?.serviceType ?? '',
      userType: currentUser?.result?.userType ?? '',
      gender: isAiInterpretationCall ? '' : (gender ?? ''),
      language: language ? language.toLowerCase() : '',
      transcribe: String(!!transcribe),
      record: String(!!record),
      meetingPin: isAiInterpretationCall ? '' : (meetingPin ?? ''),
      adastraUserID: currentUser?.result?.code ?? '',
      channel: isAiInterpretationCall ? 'audio' : (type ?? ''),
    }
  }, [userId, currentUser?.result?.serviceType, currentUser?.result?.userType, currentUser?.result?.code, type, gender, language, transcribe, record, meetingPin, isAiInterpretationCall])


  useEffect(() => {
    // console.log('=== will load amazon connect script', connectConfig)

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true

    script.innerHTML = `
      (function(w, d, x, id){
        var s=d.createElement('script');
        s.src='${activeConnectConfig.scriptUrl}';
        s.async=1;
        s.id=id;
        d.getElementsByTagName('head')[0].appendChild(s);
        w[x] =  w[x] || function() { (w[x].ac = w[x].ac || []).push(arguments) };
      })(window, document, 'amazon_connect', '${activeConnectConfig.widgetId}');
      
      amazon_connect('styles', {
        iconType: 'VOICE',
        openChat: { color: '#ffffff', backgroundColor: '${openChatBackgroundColor}' },
        closeChat: { color: '#ffffff', backgroundColor: '#123456' }
      });
      
      amazon_connect('snippetId', '${activeConnectConfig.snippetId}');

      amazon_connect('supportedMessagingContentTypes', [
        'text/plain',
        'text/markdown',
        'application/vnd.amazonaws.connect.message.interactive',
        'application/vnd.amazonaws.connect.message.interactive.response'
      ]);

      amazon_connect('contactAttributes', {${getAttributesAsString(attributes)}});
    `

    document.body.appendChild(script)

    console.log('=== amazon connect script loaded with attributes', attributes, 'using config:', activeConnectConfig)

    return () => {
      const scriptUrls = [
        connectConfig.scriptUrl,
        connectConfigVideo.scriptUrl,
        connectConfigAi.scriptUrl,
      ]
      scriptUrls.forEach((url) => {
        document.querySelectorAll<HTMLScriptElement>(`script[src="${url}"]`).forEach((s) => s.remove())
      })

      // remove ALL matching buttons on cleanup
      const chatWidgets = document.querySelectorAll<HTMLScriptElement>(
        `div[id="amazon-connect-chat-widget"]`
      )
      console.log('Chat Widgets:', chatWidgets)

      chatWidgets.forEach((c) => c.remove())

      document.getElementById('amazon-connect-dynamic-script')?.remove()
      document.getElementById('amazon-connect-chat-widget')?.remove()
      document.body.removeChild(script)
    }
  }, [attributes, type, callMode, activeConnectConfig, connectConfig, connectConfigVideo, connectConfigAi, openChatBackgroundColor])

  useEffect(() => {
    console.log('=== RequesterWidget: placeCall', placeCall)

    if (placeCall) {
      const callAttributes = {
        ...attributes,
        __ts: String(Date.now()),
      }

      if (window.amazon_connect) {
        window.amazon_connect('contactAttributes', callAttributes);
      }

      console.log('=== placing call with attributes', callAttributes)
      console.log('### query:', window.document.getElementById('amazon-connect-open-widget-button'))

      window.document.getElementById('amazon-connect-open-widget-button')?.click()
      setPlaceCall(false)
    }
  }, [placeCall, setPlaceCall])

  return null // This component doesn't render anything visible
}

export default RequesterWidget
